import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";

import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import CloseIcon from "@mui/icons-material/Close";

import surveyValidation from "./surveyValidation";
import AlertDialog from "../../Components/AlertDialog";
import i18n from "../../i18n";

const getTextByType = (error) => {
  switch (error.type) {
    case "emptyLabel":
      return {
        title: i18n.t("editor.errors.emptyLabel"),
        subtitle: i18n.t("editor.errors.requiredLabel"),
        icon: LabelOutlinedIcon,
      };
    case "duplicateLabel":
      return {
        title: i18n.t("editor.errors.duplicateLabel"),
        subtitle: i18n.t("editor.errors.uniqueLabel"),
        icon: LabelOutlinedIcon,
      };
    case "emptyConditions":
      return {
        title: i18n.t("editor.errors.emptyConditions"),
        subtitle: i18n.t("editor.errors.chooseCondition"),
        icon: SubdirectoryArrowRightIcon,
      };
    case "invalidNumber":
      return {
        title: "Not a number",
        subtitle: "Value should be a number",
        icon: ErrorOutlineOutlinedIcon,
      };
    default:
      return {
        title: "",
        subtitle: "",
        icon: ErrorOutlineOutlinedIcon,
      };
  }
};

function ErrorMessage({
  questionTree,
  details,
  handleQuestionTreeApi,
  openValidationDialog,
  handleCloseValidationDialog,
  isError,
  setIsError,
}) {
  const { t } = useTranslation();

  const [showError, setShowError] = useState(false); // show the errors on the RHS of screen
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const handleErrorItemClick = ({ id }) =>
    handleQuestionTreeApi({ type: "SCROLL_TO_QUESTION", payload: id });

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      const { errors: e, warnings: w } = surveyValidation({
        questionTree,
        details,
      });
      setErrors(e);
      setWarnings(w);
      if (e.length > 0) {
        setIsError(true);
      } else {
        setIsError(false);
      }
    }, 100);
    return () => {
      clearTimeout(timeOutId);
    };
  }, [questionTree]);

  const handleShowError = (open) => {
    setShowError(open);
    handleQuestionTreeApi({ type: "SHIFT_TO_LEFT", payload: open }); // shift the questions to left
  };

  return (
    <div>
      <Tooltip title={t("editor.errors.tooltip")}>
        <IconButton
          aria-label="Show errors"
          onClick={() => handleShowError(true)}
          size="large"
        >
          <Badge badgeContent={errors.length} color="error">
            <ErrorOutlineOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {showError ? (
        <Box position="fixed" top={84} left="auto" right={24} minWidth={300}>
          <Card variant="outlined">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingLeft={2}
              borderBottom="1px solid rgba(224, 224, 224, 1)"
            >
              <Typography variant="subtitle2">
                {t("editor.errors.title")} ({errors.length})
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => handleShowError(false)}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {errors.length > 0 ? (
              errors.slice(0, 9).map((error) => {
                const text = getTextByType(error);
                return (
                  <MenuItem
                    key={error.id}
                    onClick={() => handleErrorItemClick(error)}
                    sx={{ margin: 0, padding: 0 }}
                  >
                    <List dense={true} sx={{ margin: 0, padding: 0 }}>
                      <ListItemButton>
                        <ListItemIcon>
                          <text.icon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Tooltip title={t("editor.errors.displayQuestion")}>
                              <Box
                                display={"flex"}
                                alignItems="center"
                                sx={{ gap: 3 }}
                              >
                                <Typography variant="subtitle2">
                                  {t("editor.question")} {error.number}
                                </Typography>
                                <Typography variant="body2">
                                  {text.title}
                                </Typography>
                              </Box>
                            </Tooltip>
                          }
                          secondary={text.subtitle}
                        />
                      </ListItemButton>
                    </List>
                  </MenuItem>
                );
              })
            ) : (
              <Typography variant="body2" sx={{ padding: 2 }}>
                {t("editor.errors.solved")}
              </Typography>
            )}
          </Card>
        </Box>
      ) : null}
      <AlertDialog
        open={isError && openValidationDialog}
        setOpen={handleCloseValidationDialog}
        confirmFunction={() => handleShowError(true)}
        dialogTitle={t("editor.errors.dialogTitle")}
        dialogContentText={t("editor.errors.dialogText")}
        dialogActionConfirm={t("editor.errors.confirm")}
      />
    </div>
  );
}

export default ErrorMessage;
